import type { CSSProperties, ReactNode } from 'react';
import type { BoardRow, CategoryPublic, Judgement } from '../game/types';
import { Badge, Dossier, Stamp, StarRing, type StampVariant } from './Basics';
import { Icon } from './Icon';
import { SEQ, d } from '../motion/sequences';

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];

/* ---------------- Category heading (intro composition) ---------------- */
/** Same component for every category; content and theme come from configuration. */
export function CategoryHeading({ cat, size = 'phone' }: { cat: CategoryPublic; size?: 'phone' | 'tv' }) {
  const tv = size === 'tv', s = SEQ.categoryIntro.steps, maple = cat.theme.accent === 'maple';
  return (
    <main className="mo-curtain" style={{ position: tv ? 'absolute' : 'relative', inset: tv ? 0 : undefined, flex: tv ? undefined : 1, overflow: 'hidden', background: '#0B1F5C',
      padding: tv ? '54px 96px 96px' : '40px 22px 28px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: tv ? 30 : 18 }}>
      <span className="ex-glyph mo-fade" aria-hidden="true" style={{ ...d(s.glyph), right: tv ? 60 : -10, top: tv ? 70 : 30, fontSize: tv ? 460 : 150 }}>{cat.theme.glyph}</span>
      {tv && <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 18, background: maple ? '#D52B1E' : '#FFCC00' }} />}
      <p className="ex-mono mo-rise" style={{ ...d(s.kicker), margin: 0, fontSize: tv ? 34 : 15, color: '#FFCC00' }}>Category {cat.roman} of {ROMAN[cat.count - 1]}</p>
      <h1 className="ex-display mo-title" style={{ ...d(s.title), margin: 0, fontSize: tv ? 184 : 50, maxWidth: tv ? 1500 : undefined, textWrap: 'balance', overflowWrap: 'anywhere' } as CSSProperties}>{cat.title}</h1>
      <div style={{ display: 'flex', alignItems: 'center', gap: tv ? 48 : 0, flexWrap: 'wrap' }}>
        <Stamp variant={maple ? 'denied' : 'gold'} delay={s.stamp} size={tv ? 44 : 18}>{cat.theme.stamp}</Stamp>
        {tv && <p className="mo-rise" style={{ ...d(s.instruction), margin: 0, fontSize: 48, fontWeight: 600, maxWidth: 1100 }}>{cat.instruction}</p>}
      </div>
      {!tv && <p className="mo-rise" style={{ ...d(s.instruction), margin: 0, fontSize: 20, lineHeight: 1.4, fontWeight: 600 }}>{cat.instruction}</p>}
      <div className="mo-rise" style={{ ...d(s.chips), display: 'flex', gap: tv ? 16 : 8, flexWrap: 'wrap' }}>
        {[tv ? `${cat.formatVerb} on your phone` : cat.formatVerb, `${cat.secs} s per question`, `${cat.qCount} ${cat.qCount === 1 ? 'question' : 'questions'}`].map((t) => (
          <Badge key={t} style={tv ? { minHeight: 52, fontSize: 22, padding: '0 22px' } : undefined}>{t}</Badge>
        ))}
      </div>
    </main>
  );
}

/* ---------------- Question heading ---------------- */
export function QuestionHeading({ prompt, size = 'phone', style }: { prompt: string; size?: 'phone' | 'tv'; style?: CSSProperties }) {
  const n = prompt.length;
  const fs = size === 'tv' ? (n > 160 ? 52 : n > 90 ? 60 : 72) : (n > 140 ? 21 : n > 80 ? 24 : 27);
  return <h2 className="ex-display mo-rise" style={{ margin: 0, fontSize: fs, lineHeight: size === 'tv' ? 1.06 : 1.12, fontWeight: 700, textWrap: size === 'tv' ? 'balance' : 'pretty', overflowWrap: 'anywhere', ...style } as CSSProperties}>{prompt}</h2>;
}

/* ---------------- Result panel ---------------- */
const VERDICT: Record<Judgement['result'], [StampVariant, string, string]> = {
  correct: ['approved', 'Approved', 'Correct'], incorrect: ['denied', 'Denied', 'Incorrect'],
  partial: ['provisional', 'Partly approved', ''], none: ['void', 'No form filed', 'No answer'],
};
export function ResultPanel({ judgement }: { judgement: Judgement }) {
  const [variant, word, sub0] = VERDICT[judgement.result];
  const sub = judgement.result === 'partial' && judgement.pairs ? `${judgement.pairs[0]} of ${judgement.pairs[1]} pairs in order` : sub0;
  const s = SEQ.pointsEarned.steps;
  return (
    <section aria-label="Your result" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 92 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
        <Stamp variant={variant} delay={s.stamp} size={22}>{word}</Stamp>
        {sub && <span className="mo-fade" style={{ ...d(s.points), fontSize: 15, fontWeight: 600, color: '#B9C3E6' }}>{sub}</span>}
      </div>
      <div className="mo-points" style={{ ...d(s.points), textAlign: 'right', flex: 'none' }}>
        <div className="ex-tnum" style={{ font: '800 40px/1 var(--font-ui)', color: '#FFCC00' }}>+{judgement.points}</div>
        <div className="ex-mono" style={{ fontSize: 12, color: '#B9C3E6', marginTop: 6 }}>of {judgement.max} pts</div>
      </div>
    </section>
  );
}

/* ---------------- Explanation panel ---------------- */
export function ExplanationPanel({ text, footer, sample = true, size = 'phone', delay = 1300 }: { text: string; footer?: ReactNode; sample?: boolean; size?: 'phone' | 'tv'; delay?: number }) {
  if (size === 'tv') {
    return (
      <Dossier as="aside" tab="Examiner’s note" className="mo-rise" tabStyle={{ fontSize: 15 }} style={{ ...d(delay), flex: 1, padding: '26px 34px', display: 'flex', alignItems: 'center', gap: 28 }}>
        <p style={{ margin: 0, flex: 1, fontSize: 34, lineHeight: 1.3 }}>{text}</p>
        {sample && <Badge tone="warn" style={{ fontSize: 15 }}>Sample · unverified</Badge>}
      </Dossier>
    );
  }
  return (
    <Dossier as="aside" tab="Examiner’s note" className="mo-rise" style={{ ...d(delay), padding: '24px 18px 16px' }}>
      <p style={{ margin: 0, fontSize: 17, lineHeight: 1.45 }}>{text}</p>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <span className="ex-mono" style={{ fontSize: 12, color: '#4A4F66' }}>{footer}</span>
        {sample && <Badge tone="warn">Sample · unverified</Badge>}
      </div>
    </Dossier>
  );
}

/* ---------------- Score and rank ---------------- */
export function ScoreRank({ you, total, first }: { you: BoardRow; total: number; first: boolean }) {
  const mv = first ? { t: 'First standings of the game', i: 'same' as const } : you.delta > 0 ? { t: `Up ${you.delta} ${you.delta === 1 ? 'place' : 'places'}`, i: 'up' as const }
    : you.delta < 0 ? { t: `Down ${-you.delta} ${you.delta === -1 ? 'place' : 'places'}`, i: 'down' as const } : { t: 'Holding position', i: 'same' as const };
  return (
    <Dossier tab="Your standing" className="mo-rise" label="Your standing" style={{ ...d(150), display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr)', gap: '4px 16px', alignItems: 'end' }}>
      <span className="ex-display mo-points" style={{ ...d(600), fontSize: 64, color: '#D52B1E', gridRow: 'span 2' }}>{you.rankLabel}</span>
      <span style={{ fontSize: 16, color: '#4A4F66', fontWeight: 600 }}>of {total} entries</span>
      <span className="ex-tnum" style={{ font: '800 24px/1 var(--font-ui)' }}>{you.score} pts <span style={{ fontSize: 15, color: '#0F7B5F' }}>+{you.gained}</span></span>
      <span className="ex-badge ex-badge--paper" style={{ gridColumn: '1 / -1', justifySelf: 'start', marginTop: 8 }}><Icon name={mv.i} />{mv.t}</span>
    </Dossier>
  );
}

/* ---------------- Leaderboard ---------------- */
/**
 * One row. `fromOffset` = how many row pitches the entry travels (positive: it climbed, enters from below).
 * Identity is preserved by keying rows with the entry id.
 */
export function LeaderboardRow({ row, fromOffset = 0, pitch = 64, delay = 200, size = 'phone', showMove, leader = true }:
  { row: BoardRow; fromOffset?: number; pitch?: number; delay?: number; size?: 'phone' | 'tv'; showMove?: boolean; leader?: boolean }) {
  const tv = size === 'tv';
  const move = row.delta > 0 ? `▲${row.delta}` : row.delta < 0 ? `▼${-row.delta}` : '';
  return (
    <li className="ex-lbrow mo-lbmove" data-you={row.isYou} data-top={leader && row.rank === 1 ? '1' : '0'}
      style={{ ['--dy' as string]: `${fromOffset * pitch}px`, ...d(delay), ...(tv ? { minHeight: 78, gridTemplateColumns: '96px minmax(0,1fr) auto', padding: '0 28px', gap: 20 } : null) }}>
      <span className="ex-lbrow__rank" style={tv ? { fontSize: 42 } : undefined}>{row.rank}</span>
      <span className="ex-lbrow__name" style={tv ? { fontSize: 38 } : undefined}>{row.name}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: tv ? 18 : 8 }}>
        {showMove && move && <span className="ex-mono" style={{ fontSize: tv ? 20 : 12, opacity: 0.8 }}>{move}</span>}
        <span className="ex-lbrow__score" style={tv ? { fontSize: 40 } : undefined}>{row.score}</span>
      </span>
    </li>
  );
}

/** Phone list: top 3, a gap marker, then the player's neighbours (even far outside the top group). */
export function LeaderboardNear({ rows, you, first }: { rows: BoardRow[]; you: BoardRow | null; first: boolean }) {
  const top = rows.slice(0, 3);
  const near = you && you.index >= 3 ? rows.slice(Math.max(3, you.index - 1), you.index + 2) : rows.slice(3, 5);
  const gap = near.length > 0 && near[0].index > 3;
  const off = (r: BoardRow) => (first ? 0.5 : Math.max(-3, Math.min(3, r.delta)));
  return (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }} aria-label="Leading entries and your neighbours">
      {top.map((r, i) => <LeaderboardRow key={r.id} row={r} fromOffset={off(r)} delay={SEQ.leaderboardMove.steps.move + i * 110} />)}
      {gap && <li className="ex-lbgap" aria-hidden="true">· · ·</li>}
      {near.map((r, i) => <LeaderboardRow key={r.id} row={r} fromOffset={off(r)} delay={SEQ.leaderboardMove.steps.move + (i + 3) * 110} leader={false} />)}
    </ol>
  );
}

/* ---------------- Countdown numeral ---------------- */
export function Countdown({ remaining, size }: { remaining: number; size: number }) {
  return (
    <StarRing ornament size={size} className="mo-ring" starSize="12%">
      <span key={remaining} className="ex-timer__num ex-display mo-count" style={{ fontSize: size * 0.53 }}>{Math.max(1, remaining)}</span>
    </StarRing>
  );
}
